import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { usePatients, usePayers, usePlans, useDiscrepancies, useEligibility, usePractices } from '../api/hooks';
import { fmtDob } from '../lib/data';
import PageHeader from '../components/PageHeader';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import StatusPill from '../components/StatusPill';
import EmptyState from '../components/EmptyState';
import { Search, Users, AlertTriangle } from 'lucide-react';

export default function Patients() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const scopeAll = practiceId === 'all';
  const [q, setQ] = useState('');
  const [payer, setPayer] = useState('all');

  const { data: inPractice = [] } = usePatients(practiceId, q || undefined);
  const { data: payersAll = [] } = usePayers();
  const { data: plansAll = [] } = usePlans();
  const { data: practices = [] } = usePractices();
  const { data: discrepancies = [] } = useDiscrepancies(practiceId);
  const { data: checks = [] } = useEligibility(practiceId);

  const payerById = (id) => payersAll.find((p) => p.id === id);
  const planById = (id) => plansAll.find((p) => p.id === id);
  const practiceById = (id) => practices.find((p) => p.id === id);

  // Pending discrepancies per patient, mirroring discrepanciesForPatient(id).
  const pendingDiscByPatient = useMemo(() => {
    const map = {};
    discrepancies.filter((d) => d.status === 'pending').forEach((d) => { map[d.patientId] = (map[d.patientId] || 0) + 1; });
    return map;
  }, [discrepancies]);

  // Latest check per patient, mirroring latestCheckForPatient(id).
  const latestCheckByPatient = useMemo(() => {
    const map = {};
    [...checks]
      .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
      .forEach((c) => { if (!map[c.patientId]) map[c.patientId] = c; });
    return map;
  }, [checks]);

  const payers = useMemo(() => Array.from(new Map(inPractice.map((p) => [p.payerId, payerById(p.payerId)]).filter(([id, pp]) => id && pp)).values()), [inPractice, payersAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const patients = useMemo(() =>
    inPractice
      .filter((p) => payer === 'all' || p.payerId === payer)
      .filter((p) => !q || `${p.firstName} ${p.lastName} ${p.memberIdLast4}`.toLowerCase().includes(q.toLowerCase())),
    [inPractice, q, payer]);

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Directory" title="Patients" subtitle="Records synced from your practice management system. Eligibility runs only when a patient has an appointment." />
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9 bg-white h-9" placeholder="Search name or member ID…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Select value={payer} onValueChange={setPayer}><SelectTrigger className="h-9 w-auto min-w-[130px] bg-white text-sm"><SelectValue placeholder="Payer" /></SelectTrigger><SelectContent><SelectItem value="all">All payers</SelectItem>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <span className="text-xs text-slate-500 tnum">{patients.length} patients</span>
        </div>
      </div>

      <div className="dh-card-flush overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50/70">
            <tr>{['Patient', ...(scopeAll ? ['Practice'] : []), 'DOB', 'Payer / plan', 'Member', 'Last check', 'Data'].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {patients.map((p) => {
              const payer = payerById(p.payerId);
              const plan = planById(p.planId);
              const discCount = pendingDiscByPatient[p.id] || 0;
              const check = latestCheckByPatient[p.id];
              return (
                <tr key={p.id} className="border-t border-slate-100 row-hover">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">{p.firstName} {p.lastName}{p.isNewPatient && <span className="text-[10px] text-brand-700 bg-brand-50 border border-brand-100 px-1.5 rounded">NEW</span>}</div>
                    <div className="text-xs text-slate-500">{p.phone}</div>
                  </td>
                  {scopeAll && <td className="px-4 py-3 text-slate-600 text-xs">{practiceById(p.practiceId)?.name}</td>}
                  <td className="px-4 py-3 text-slate-700 tnum">{fmtDob(p.dob)}</td>
                  <td className="px-4 py-3"><div className="text-slate-900">{plan?.planName}</div><div className="text-xs text-slate-500">{payer?.name}</div></td>
                  <td className="px-4 py-3 text-slate-700 mono">••••{p.memberIdLast4}</td>
                  <td className="px-4 py-3">{check ? <StatusPill status={check.networkStatusResolved} /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {discCount > 0 ? (
                      <Link to="/app/appointments" className="pill pill-amber"><AlertTriangle className="h-3 w-3" />{discCount} mismatch</Link>
                    ) : <span className="pill pill-green">Synced</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {patients.length === 0 && <EmptyState icon={Users} title="No patients found" hint="Try a different search." />}
      </div>
    </div>
  );
}
